/**
 * SettingsAiSection — extracted AI configuration cards from SettingsPage.jsx.
 *
 * Iter 137 (Feb 2026). Contains: AI Auto-Reply, Smart Priority Queue,
 * AI Rules & Instructions, AI Learning. All 4 cards are admin/super_admin
 * gated. State and handlers stay in SettingsPage and are passed as props,
 * so the contract is verbose but explicit and matches the iter-135
 * ChatListSidebar pattern.
 */

import { Loader2, Save, MessageSquare, Sparkles, Zap, Brain, Clock, Trash2, Power, PowerOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { aiBulkApi } from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SettingsAiSection({
  user,
  // Auto-reply
  autoReply, setAutoReply, savingAutoReply, saveAutoReply,
  // Priority queue
  priorityQueue, togglePriorityQueue,
  // AI rules
  aiRules, aiRulesText, setAiRulesText, savingAiRules, handleSaveAiRules,
  // AI learning
  aiKnowledge, learningInProgress, triggerAiLearning,
}) {
  return (
    <>
        {/* AI Auto-Reply Card - Admin Only */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card lg:col-span-2" data-testid="auto-reply-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-md"><MessageSquare size={20} className="text-orange-700" /></div>
                <div>
                  <h2 className="text-lg font-bold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>AI Auto-Reply</h2>
                  <p className="text-xs text-[--text-muted]">Automatically reply to customers when agents are offline</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={autoReply.enabled} onChange={(e) => setAutoReply(s => ({ ...s, enabled: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-orange-600" data-testid="auto-reply-enabled" />
                  <span className="text-sm font-semibold">{autoReply.enabled ? "Enabled" : "Disabled"}</span>
                </label>
                <button onClick={saveAutoReply} disabled={savingAutoReply} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1" data-testid="save-auto-reply-btn">
                  {savingAutoReply ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
                </button>
                <button
                  onClick={async () => {
                    try {
                      const preview = await axios.post(`${API}/api/admin/cleanup-ai-messages?hours=72&dry_run=true`);
                      const toDelete = preview.data?.would_delete || 0;
                      const protectedCount = preview.data?.protected_sent_ai_messages || 0;
                      if (toDelete === 0 && protectedCount === 0) {
                        toast.success("No AI messages found in the last 72 hours.");
                        return;
                      }
                      const msg = `Found ${toDelete} undelivered AI message(s) from last 72h.\n` +
                        `(${protectedCount} AI message(s) were successfully sent to customers via WATI and are PROTECTED — they will NOT be deleted.)\n\n` +
                        `Delete the ${toDelete} undelivered one(s)?`;
                      if (toDelete === 0) {
                        toast.info(`${protectedCount} AI messages were successfully sent and kept safely.`);
                        return;
                      }
                      if (!window.confirm(msg)) return;
                      const res = await axios.post(`${API}/api/admin/cleanup-ai-messages?hours=72`);
                      toast.success(`Deleted ${res.data?.deleted || 0} undelivered AI message(s). ${res.data?.protected_sent_ai_messages || 0} delivered one(s) kept safely.`);
                    } catch (err) {
                      toast.error("Cleanup failed: " + (err?.response?.data?.detail || err.message));
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm font-bold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 flex items-center gap-1"
                  data-testid="cleanup-ai-messages-btn"
                  title="Delete AI messages that were NOT successfully sent to customers via WATI. Messages already delivered are protected."
                >
                  <Trash2 size={14} /> Clean Failed AI Messages
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {/* Office Hours explanation */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-800">
                <p className="font-bold mb-1">🕐 Out-of-Office Mode</p>
                <p>AI replies automatically <b>outside</b> your office hours (IST). Inside office hours your team handles chats manually.</p>
              </div>
              {/* Schedule: Days */}
              <div>
                <label className="text-xs font-bold text-[--text-muted] uppercase tracking-wider mb-2 block">Active Days</label>
                <div className="flex gap-2 flex-wrap" data-testid="auto-reply-days">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                    <button key={d} onClick={() => {
                      const days = autoReply.active_days || autoReply.schedule?.days || [];
                      const next = days.includes(i) ? days.filter(x => x !== i) : [...days, i];
                      setAutoReply(s => ({ ...s, active_days: next, schedule: { ...s.schedule, days: next } }));
                    }}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${((autoReply.active_days || autoReply.schedule?.days) || []).includes(i) ? "bg-[--accent] text-white border-[--accent]" : "bg-white text-[--text-secondary] border-[--border-dark]"}`}
                      data-testid={`auto-reply-day-${i}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              {/* Office hours */}
              <div>
                <label className="text-xs font-bold text-[--text-muted] uppercase tracking-wider mb-2 block">Office Hours (IST) — AI is OFF during this window</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="time"
                    value={autoReply.office_open || "09:30"}
                    onChange={(e) => setAutoReply(s => ({ ...s, office_open: e.target.value, mode: "office_hours" }))}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                    data-testid="auto-reply-office-open"
                  />
                  <span className="text-sm text-[--text-muted] font-medium">to</span>
                  <input
                    type="time"
                    value={autoReply.office_close || "18:30"}
                    onChange={(e) => setAutoReply(s => ({ ...s, office_close: e.target.value, mode: "office_hours" }))}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                    data-testid="auto-reply-office-close"
                  />
                  <span className="text-xs text-[--text-muted]">
                    AI replies outside this window on selected days
                  </span>
                </div>
              </div>
              {/* Transfer message */}
              <div>
                <label className="text-xs font-bold text-[--text-muted] uppercase tracking-wider mb-1 block">Transfer Message (sent when AI hands off to agent)</label>
                <textarea value={autoReply.transfer_message || ""} onChange={(e) => setAutoReply(s => ({ ...s, transfer_message: e.target.value }))}
                  rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="I'll connect you with our team..." data-testid="auto-reply-transfer-msg" />
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-xs text-orange-700 space-y-1">
                <p className="font-bold">AI Rules (built-in):</p>
                <p>- Greets warmly, shares product links only</p>
                <p>- Shares details only when customer asks</p>
                <p>- Transfers to agent for: price queries, delivery estimates (books), photo/video requests</p>
                <p>- Books: shipping & handling included (India, when asked) | Other: free shipping (when asked)</p>
                <p>- Never mentions external links (Amazon, Flipkart, etc.)</p>
              </div>

              {/* Iter 139 (Feb 2026) — ad-hoc bulk toggle for sale-day situations */}
              <div className="border-t border-[--border] pt-3 mt-2" data-testid="ai-bulk-toggle-row">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[--text-primary] flex items-center gap-1">
                      <Power size={14} className="text-emerald-600" /> Bulk AI takeover (ad-hoc)
                    </p>
                    <p className="text-[11px] text-[--text-muted] mt-0.5">
                      Flip every <b>currently OPEN</b> chat to AI auto-reply right now. Useful on a sale day, festival rush, or when the team is in a meeting. Skips SOLVED / PENDING / EXPIRED chats so resolved threads stay quiet.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={async () => {
                        if (!window.confirm("Turn ON AI auto-reply on ALL currently OPEN chats?\n\nThis will not touch solved or pending chats. You can switch them back any time.")) return;
                        try {
                          const r = await aiBulkApi.enableAll("manual_settings_button");
                          toast.success(`AI enabled on ${r.data?.updated || 0} open chat(s)`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Bulk enable failed");
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                      data-testid="ai-bulk-enable-btn"
                    >
                      <Power size={12} /> Enable on all open chats
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm("Turn OFF AI on all currently OPEN chats?\n\nThis only flips the per-chat AI flag. Outside-office-hours auto-reply still applies based on your global rules above.")) return;
                        try {
                          const r = await aiBulkApi.disableAll();
                          toast.success(`AI disabled on ${r.data?.updated || 0} chat(s)`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Bulk disable failed");
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 inline-flex items-center gap-1"
                      data-testid="ai-bulk-disable-btn"
                    >
                      <PowerOff size={12} /> Disable
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Smart Priority Queue - Admin Only */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card" data-testid="priority-queue-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-md"><Zap size={20} className="text-blue-700" /></div>
                <div>
                  <h2 className="text-lg font-bold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>Smart Priority Queue</h2>
                  <p className="text-xs text-[--text-muted]">Auto-sort chats by urgency (wait time + sentiment + VIP label)</p>
                </div>
              </div>
              <button onClick={togglePriorityQueue}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${priorityQueue ? "bg-[--accent] text-white" : "bg-gray-100 text-gray-600 border-2 border-gray-200"}`}
                data-testid="priority-queue-toggle">
                {priorityQueue ? "Enabled" : "Disabled"}
              </button>
            </div>
          </div>
        )}

        {/* AI Rules Card - Admin Only. Free-text instructions that steer every AI reply. */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card lg:col-span-2" data-testid="ai-rules-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-md"><Sparkles size={20} className="text-indigo-700" /></div>
                <div>
                  <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>AI Rules & Instructions</h2>
                  <p className="text-xs text-[#57534E]">Type rules here in plain English — every AI reply will follow them (highest priority)</p>
                </div>
              </div>
              <button
                onClick={handleSaveAiRules}
                disabled={savingAiRules}
                className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1"
                data-testid="save-ai-rules-btn"
              >
                {savingAiRules ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save Rules</>}
              </button>
            </div>
            <div className="space-y-2">
              {/* Learned patterns (read-only) — click Append to move into the rules textarea */}
              {aiKnowledge?.learnings && (
                <details className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 group" data-testid="learned-patterns-panel">
                  <summary className="flex items-center justify-between cursor-pointer text-xs font-bold text-purple-700">
                    <span className="flex items-center gap-1.5">
                      <Brain size={12} /> Machine-learned patterns ({aiKnowledge.conversations_analyzed || 0} conversations) — click to expand
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const prefix = aiRulesText.trim() ? "\n\n" : "";
                        const next = `${aiRulesText}${prefix}# Learned patterns (auto-analyzed on ${new Date(aiKnowledge.last_updated).toLocaleDateString("en-IN")})\n${aiKnowledge.learnings}`;
                        if (next.length > 10000) {
                          toast.error(`Cannot append: would exceed 10,000 characters (would be ${next.length})`);
                          return;
                        }
                        setAiRulesText(next);
                        toast.success("Learned patterns appended — review and edit, then click Save Rules");
                      }}
                      className="text-[10px] px-2 py-0.5 bg-purple-600 text-white rounded-full hover:bg-purple-700"
                      data-testid="append-learnings-btn"
                    >
                      + Append to rules
                    </button>
                  </summary>
                  <pre className="mt-2 text-[11px] text-[#57534E] whitespace-pre-wrap max-h-[220px] overflow-y-auto font-mono">{aiKnowledge.learnings}</pre>
                </details>
              )}

              <textarea
                value={aiRulesText}
                onChange={(e) => setAiRulesText(e.target.value)}
                placeholder={`Write any rules for the AI here. Examples:

• For books, always mention Ships within 3-5 days unless leadtime says otherwise.
• Never quote prices to international customers without confirming country first.
• If customer asks about discount, say: "Our team reviews discount requests — could you share which product you are interested in?"
• For Bengali language customers, reply in Hindi with simple wording.
• If customer mentions wedding or gift, suggest our Jewelry collection.

Keep it simple — the AI will read this every time it replies.`}
                className="w-full border-2 border-[#E7E5E4] rounded-lg p-3 text-sm font-mono bg-white focus:border-indigo-400 focus:outline-none"
                rows={14}
                maxLength={10000}
                data-testid="ai-rules-textarea"
              />
              <div className="flex items-center justify-between text-xs text-[#57534E]">
                <span>{aiRulesText.length} / 10,000 characters</span>
                {aiRules?.updated_at && (
                  <span>Last saved: {new Date(aiRules.updated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} by {aiRules.updated_by || "admin"}</span>
                )}
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 text-[11px] text-indigo-800">
                💡 Tip: Rules here are applied <strong>on top of</strong> built-in business rules and any learned patterns. The AI follows these first. Changes take effect immediately — no restart needed.
              </div>
            </div>
          </div>
        )}

        {/* AI Learning Card - Admin Only */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card lg:col-span-2" data-testid="ai-learning-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-md"><Brain size={20} className="text-purple-700" /></div>
                <div>
                  <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>AI Learning</h2>
                  <p className="text-xs text-[#57534E]">AI studies agent conversations to improve reply suggestions</p>
                </div>
              </div>
              <button onClick={triggerAiLearning} disabled={learningInProgress} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1" data-testid="trigger-learning-btn">
                {learningInProgress ? <Loader2 size={14} className="animate-spin" /> : <><Zap size={14} /> Learn Now</>}
              </button>
            </div>
            {aiKnowledge?.last_updated && (
              <div className="bg-purple-50 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 text-xs text-purple-600 mb-2">
                  <Clock size={12} /> Last updated: {new Date(aiKnowledge.last_updated).toLocaleString("en-IN")} | {aiKnowledge.conversations_analyzed} conversations analyzed
                </div>
                <details className="cursor-pointer">
                  <summary className="text-xs font-medium text-purple-700">View AI Learnings</summary>
                  <pre className="mt-2 text-xs text-[#57534E] whitespace-pre-wrap max-h-[300px] overflow-y-auto">{aiKnowledge.learnings}</pre>
                </details>
              </div>
            )}
            {!aiKnowledge?.last_updated && (
              <p className="text-sm text-[#57534E]">No learnings yet. Click "Learn Now" to analyze agent conversations and build the knowledge base. In production, this runs automatically every 6 hours.</p>
            )}
          </div>
        )}
    </>
  );
}
